import type { Product, Offer } from '../types';

// Base path for product images - added automatically to image filenames
const IMAGE_BASE_PATH = '/images/products/';

/**
 * Add the base image path prefix to a filename
 */
function addImagePath(filename: string): string {
  if (!filename) return '';
  // If already has full path, return as-is
  if (filename.startsWith('/')) return filename;
  return `${IMAGE_BASE_PATH}${filename}`;
}

/**
 * Parse a CSV string into an array of products
 * CSV Format: Comma-separated with special handling:
 * - images array: pipe (|) separated
 * - ingredients: semicolon (;) separated (joined with comma for display)
 * - offer fields: offer_originalPrice, offer_discount, offer_offerName
 * - Image paths: Only filename needed, /images/products/ is added automatically
 */
export function parseCSVToProducts(csvContent: string): Product[] {
  const lines = csvContent.trim().split('\n');
  if (lines.length < 2) return [];

  // Parse header row
  const headers = parseCSVLine(lines[0]);
  
  // Parse data rows
  const products: Product[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    
    const values = parseCSVLine(line);
    const row: Record<string, string> = {};
    
    headers.forEach((header, index) => {
      row[header] = values[index]?.trim() || '';
    });
    
    // Build product object
    // Determine the price: if there's an offer with offer_offerName, use that as the discounted price
    // Otherwise, use the regular price column
    let productPrice = parseFloat(row.price);
    
    // Check if there's an offer and calculate discounted price
    if (row.is_offer && row.is_offer.trim().toLowerCase() === 'true') {
      if (row.offer_offerName?.trim()) {
        // Use the discounted price from offer_offerName
        productPrice = parseFloat(row.offer_offerName);
      } else if (row.offer_originalPrice?.trim() && row.offer_discount?.trim()) {
        // Calculate discounted price if offer_offerName is missing
        const originalPrice = parseFloat(row.offer_originalPrice);
        const discount = parseFloat(row.offer_discount);
        productPrice = originalPrice * (1 - discount / 100);
      }
    }
    
    const product: Product = {
      id: row.id,
      name: row.name,
      price: productPrice,
      image: addImagePath(row.image),
    };
    
    // Optional fields - only add if non-empty
    if (row.backImage && row.backImage.trim()) {
      product.backImage = addImagePath(row.backImage);
    }
    
    if (row.description && row.description.trim()) {
      product.description = row.description;
    }
    
    if (row.ingredients && row.ingredients.trim()) {
      // Convert semicolon-separated to comma-separated for display
      product.ingredients = row.ingredients.split(';').map(s => s.trim()).join(', ');
    }
    
    if (row.images && row.images.trim()) {
      // Parse pipe-separated images and add path prefix to each
      const imageList = row.images
        .split('|')
        .map(s => s.trim())
        .filter(Boolean)
        .map(addImagePath);
      if (imageList.length > 0) {
        product.images = imageList;
      }
    }
    
    if (row.tag && row.tag.trim()) {
      product.tag = row.tag;
    }
    
    if (row.weight && row.weight.trim()) {
      product.weight = row.weight.trim();
    }
    
    if (row.is_offer && row.is_offer.trim().toLowerCase() === 'true') {
      product.isOffer = true;
      
      // Parse offer ONLY if is_offer is true
      if (row.offer_originalPrice?.trim() && row.offer_discount?.trim()) {
        const offer: Offer = {
          originalPrice: parseFloat(row.offer_originalPrice),
          discount: parseFloat(row.offer_discount),
        };
        
        if (row.offer_offerName?.trim()) {
          offer.offerName = row.offer_offerName;
        }
        
        product.offer = offer;
      }
    }
    
    products.push(product);
  }
  
  return products;
}

/**
 * Parse a single CSV line, handling quoted fields with commas
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  
  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    
    if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  
  // Don't forget the last field
  result.push(current.trim());
  
  return result;
}

/**
 * Load products from CSV file
 */
export async function loadProductsFromCSV(csvPath: string = '/data/products.csv'): Promise<Product[]> {
  try {
    const response = await fetch(csvPath);
    if (!response.ok) {
      throw new Error(`Failed to load CSV: ${response.status}`);
    }
    const csvContent = await response.text();
    return parseCSVToProducts(csvContent);
  } catch (error) {
    console.error('Error loading products from CSV:', error);
    return [];
  }
}
